export const validateField = (fieldName, value, formData = {}) => {
  switch (fieldName) {
    case "name": {
      const trimmed = value ? String(value).trim() : "";
      if (!trimmed) return "Template name is mandatory.";
      if (trimmed.length < 3) return "Template name must be at least 3 characters.";
      if (trimmed.length > 60) return "Template name cannot exceed 60 characters.";
      if (!/^[A-Za-z0-9 .'-]+$/.test(trimmed)) {
        return "The name may only contain letters, numbers, spaces, dots, apostrophes, and hyphens.";
      }
      return "";
    }

    case "difficulty":
      return !["beginner", "intermediate", "advanced"].includes(value)
        ? "Please select a valid difficulty."
        : "";

    case "categories":
      return !value || !Array.isArray(value) || value.length === 0
        ? "At least one category is required."
        : "";

    case "target_tenant_id":
      if (formData.tenant_mode === "specific" && !value) {
        return "Tenant ID is required for specific mode.";
      }
      return "";

    case "image_path":
      if (value) {
        const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
        if (!validTypes.includes(value.type)) return "Thumbnail must be jpeg, png, jpg, or gif.";
        if (value.size > 2 * 1024 * 1024) return "Thumbnail size cannot exceed 2MB.";
      }
      return "";

    case "language_mappings": {
      if (!value || !Array.isArray(value) || value.length === 0) {
        return "At least one language mapping is required.";
      }

      const languageIds = value
        .map((m) => (m.language_id !== "" && m.language_id !== null ? String(m.language_id) : ""))
        .filter(Boolean);

      const uniqueLanguageIds = new Set(languageIds);
      if (languageIds.length !== uniqueLanguageIds.size) {
        return "A language can only be selected once per template.";
      }

      for (let i = 0; i < value.length; i++) {
        const map = value[i];
        if (!map.language_id) return `Row ${i + 1}: Language is required.`;
        if (!map.email_template_id) return `Row ${i + 1}: Email Template is required.`;
        if (!map.landing_page_id) return `Row ${i + 1}: Landing Page is required.`;
        if (!map.sending_profile_id) return `Row ${i + 1}: Sending Profile is required.`;
        if (!map.phishing_domain_id) return `Row ${i + 1}: Phishing Domain is required.`;
      }
      return "";
    }

    default:
      return "";
  }
};

export const validateAllFormFields = (formData) => {
  const newErrors = {};

  const nameErr = validateField("name", formData.name, formData);
  if (nameErr) newErrors.name = nameErr;

  const diffErr = validateField("difficulty", formData.difficulty, formData);
  if (diffErr) newErrors.difficulty = diffErr;

  const catErr = validateField("categories", formData.categories, formData);
  if (catErr) newErrors.categories = catErr;

  if (formData.tenant_mode === "specific") {
    const tenantErr = validateField("target_tenant_id", formData.target_tenant_id, formData);
    if (tenantErr) newErrors.target_tenant_id = tenantErr;
  }

  const imgErr = validateField("image_path", formData.image_path, formData);
  if (imgErr) newErrors.image_path = imgErr;

  const mappingErr = validateField("language_mappings", formData.language_mappings, formData);
  if (mappingErr) newErrors.language_mappings = mappingErr;

  return {
    isValid: Object.keys(newErrors).length === 0,
    errors: newErrors,
  };
};

export const parseBackendErrors = (error) => {
  const resData = error?.response?.data;
  const backendErrorsList = resData?.errors;
  const exceptionMessage = resData?.message;

  const fieldErrors = {};
  const genericErrors = [];

  if (Array.isArray(backendErrorsList)) {
    backendErrorsList.forEach((err) => {
      const lower = err.toLowerCase();

      if (
        lower.includes("language") ||
        lower.includes("mapping") ||
        lower.includes("email_template") ||
        lower.includes("landing_page") ||
        lower.includes("sending_profile") ||
        lower.includes("phishing_domain") ||
        lower.includes("template id") ||
        lower.includes("page id") ||
        lower.includes("profile id") ||
        lower.includes("domain id")
      ) {
        fieldErrors.language_mappings = err;
      } else if (
        lower.includes("template name") ||
        lower.includes("name is mandatory") ||
        lower.includes("name already exists") ||
        (lower.includes("name") && !lower.includes("language"))
      ) {
        fieldErrors.name = err;
      } else if (lower.includes("difficulty")) {
        fieldErrors.difficulty = err;
      } else if (lower.includes("categor")) {
        fieldErrors.categories = err;
      } else if (lower.includes("tenant")) {
        fieldErrors.target_tenant_id = err;
      } else if (lower.includes("thumbnail") || lower.includes("image")) {
        fieldErrors.image_path = err;
      } else {
        genericErrors.push(err);
      }
    });
  } else if (backendErrorsList && typeof backendErrorsList === "object") {
    Object.entries(backendErrorsList).forEach(([key, val]) => {
      const errText = Array.isArray(val) ? val[0] : val;
      if (key.startsWith("language_mappings.") || key === "language_mappings") {
        fieldErrors.language_mappings = errText;
      } else if (key.startsWith("categories.") || key === "categories") {
        fieldErrors.categories = errText;
      } else {
        fieldErrors[key] = errText;
      }
    });
  } else if (exceptionMessage) {
    const lowerMsg = exceptionMessage.toLowerCase();
    if (lowerMsg.includes("template name") || lowerMsg.includes("name already exists")) {
      fieldErrors.name = exceptionMessage;
    } else if (lowerMsg.includes("language") || lowerMsg.includes("mapping")) {
      fieldErrors.language_mappings = exceptionMessage;
    } else {
      genericErrors.push(exceptionMessage);
    }
  } else {
    genericErrors.push("An unexpected error occurred during submission.");
  }

  return {
    fieldErrors,
    backendErrorText: genericErrors.join(" | "),
  };
};